/**
 * THE declaration of the `/settings` surface: one row per routed settings path, each
 * carrying where it appears in the Settings menu.
 *
 * WHY THIS FILE EXISTS. The Settings menu used to be a hand-written array inside
 * `Layout.tsx`, kept in step with `App.tsx`'s route table by memory alone. It drifted:
 * `/settings/quality` and `/settings/audit` were routed, their pages worked, and neither
 * was in the menu — two finished features reachable only by typing a URL. The operator
 * reported "there is no UI for tuning quality"; there was, it was unlinked. That is the
 * tenth instance in this codebase of a feature that landed and was never wired to its
 * entry point.
 *
 * So the menu is no longer written. It is DERIVED from the rows below, and `Layout.tsx`
 * holds no `/settings` path of its own — `tests/test_settings_menu_covers_every_route.py`
 * fails if it grows one.
 *
 * WHY THE ROUTES ARE STILL LITERAL JSX IN `App.tsx`, and not generated from this table:
 * `tests/test_settings_guarantees.py` parses `App.tsx`'s own `<Route path="/settings/…"
 * element={<Component />} />` literals to pair every settings surface with its page and
 * its API, and asserts it found some ("App.tsx yielded no /settings routes — the
 * derivation is broken"). Generating the routes would blind that guard. The route table
 * therefore stays the source of truth for what EXISTS, this table declares where each
 * one APPEARS, and the guard above compares the two sets in both directions.
 *
 * ADDING A SETTINGS PAGE: add the `<Route>` to `App.tsx` and a row here. There is no
 * default placement and no optional field to forget — `MenuPlacement` is a discriminated
 * union, so a row that decides nothing does not compile, and a route with no row at all
 * fails the guard.
 */

/**
 * Where a settings surface appears in the menu.
 *
 * `hidden` is deliberate and must say why, because it is the one placement that
 * reproduces the original defect on purpose. It exists for a path that is routed but
 * must not be offered: a kept alias, or a parameterised path whose real entries are
 * enumerated at runtime.
 */
export type MenuPlacement =
  | { readonly kind: 'top'; readonly label: string }
  | { readonly kind: 'child'; readonly label: string; readonly under: string }
  | { readonly kind: 'hidden'; readonly reason: string }

export interface SettingsSurface {
  /** The routed path, exactly as `App.tsx` spells it. */
  readonly path: string
  readonly placement: MenuPlacement
}

/** The Provider Accounts surface, referenced by its kind children and by `Layout.tsx`. */
export const PROVIDER_ACCOUNTS_PATH = '/settings/provider-accounts'

/**
 * Declared in menu order. Top-level rows render in the order they appear here; a `child`
 * row renders under its parent, in this order, after any children grafted on at runtime.
 */
export const SETTINGS_SURFACES: readonly SettingsSurface[] = [
  { path: '/settings/metrics', placement: { kind: 'top', label: 'Metrics Settings' } },
  { path: '/settings/system', placement: { kind: 'top', label: 'System' } },
  // U3a. Routed since the quality-config API shipped, absent from the menu until now:
  // the page an operator reaches for when a received fax is being judged too harshly.
  { path: '/settings/quality', placement: { kind: 'top', label: 'Quality' } },
  { path: '/settings/slack', placement: { kind: 'top', label: 'Slack' } },
  { path: PROVIDER_ACCOUNTS_PATH, placement: { kind: 'top', label: 'Provider Accounts' } },
  {
    // U3b. SFax was listed BOTH here, as a second-level Settings entry, and under Provider
    // Accounts as the `sfax` account kind — the same accounts behind two menu paths, which
    // reads as two different things to configure. It belongs under Providers, so this row
    // is hidden rather than deleted: the route stays, because it is an operator bookmark
    // and 59 tests target the page behind it (see `App.tsx`).
    path: '/settings/sfax-accounts',
    placement: {
      kind: 'hidden',
      reason:
        'Kept as a bookmarkable alias. SFax is offered once, under Provider Accounts, as ' +
        'the sfax account kind — listing it here too showed one set of accounts twice.',
    },
  },
  {
    // The kind-parameterised surface. Its menu entries are the real kinds, grafted onto
    // Provider Accounts at render time from GET /api/settings/provider-accounts/kinds,
    // because the kind vocabulary is declared in the provider registry
    // (`LegCredentialSpec.account_kind`). Enumerating them here would be a second
    // declaration of it, and the two would drift the moment a provider was added.
    path: '/settings/provider-accounts/:kind',
    placement: {
      kind: 'hidden',
      reason:
        'Parameterised. The concrete kind entries are read from the provider registry at ' +
        'render time so the menu cannot lag a newly added provider.',
    },
  },
  { path: '/settings/identity', placement: { kind: 'top', label: 'Identity' } },
  // B3. The relay agents: their credentials, their reported target inventory, and how much
  // work each is holding. A TOP-LEVEL row rather than a child of Provider Accounts: an
  // agent is not a provider credential — it is a deployment of ours inside someone else's
  // account — and the two are configured on different occasions by different people.
  //
  // THIS ROW RETIRES AN EXEMPTION. `tests/test_settings_guarantees.py` carried
  // `API_ONLY_SURFACES = {"agents"}` with `page=None`, added when the router landed ahead
  // of its UI. This page is that UI, so the key is gone from that set and the row names
  // `AgentSettings`; the guard asserts both directions, so doing one without the other
  // fails rather than leaving a dead concession behind.
  { path: '/settings/agents', placement: { kind: 'top', label: 'Agents' } },
  { path: '/settings/logs', placement: { kind: 'top', label: 'Logs' } },
  {
    // U3a, then FINISH-LINE A7.19. The audit log IS the Audit pane of Logs — it has no
    // page of its own: `AuditLogPage` is a `<Navigate to="/settings/logs?pane=audit" />`
    // redirect, and `AuditLogPane` renders inside `LogViewer`. U3a added this row because
    // the pane was unreachable from the menu at all; that is no longer true, because `Logs`
    // above is a top-level entry and the pane selector on that page offers Audit. So the
    // row was a second menu path to a pane its own parent already reaches — the same
    // one-thing-listed-twice shape as `/settings/sfax-accounts` below.
    //
    // THE ROUTE STAYS, and hidden is the right placement rather than deletion:
    //   * it is a bookmarkable alias. The URL is in bookmarks and in links people have
    //     already sent each other (see the header of `pages/AuditLog.tsx`), and a dead
    //     settings URL landing on the dashboard reads as "the audit log was removed".
    //   * `tests/test_settings_guarantees.py` PARSES `App.tsx`'s route literals to pair
    //     every settings surface with its page component and its API, and resolves
    //     `AuditLogPage` through the import. Deleting the route removes a pairing that
    //     guard checks, for no operator gain.
    path: '/settings/audit',
    placement: {
      kind: 'hidden',
      reason:
        'Kept as a bookmarkable alias, and it redirects rather than rendering a page of ' +
        'its own. The audit log is offered once, as the Audit pane of Logs — listing it ' +
        'here too was a second menu path to a pane its own parent already reaches.',
    },
  },
]

/** One rendered menu row. `children` is one nested level and no more. */
export interface SettingsMenuItem {
  path: string
  label: string
  children?: { path: string; label: string }[]
}

/**
 * Fold the declaration into the menu.
 *
 * `extraChildren` carries the children that cannot be declared statically — today the
 * provider account kinds. They are listed before the declared children of the same
 * parent so a runtime-discovered entry never hides behind a static one.
 */
export function buildSettingsMenu(
  extraChildren: Readonly<Record<string, { path: string; label: string }[]>> = {},
): SettingsMenuItem[] {
  const items: SettingsMenuItem[] = []
  const byPath = new Map<string, SettingsMenuItem>()

  for (const surface of SETTINGS_SURFACES) {
    if (surface.placement.kind !== 'top') continue
    const item: SettingsMenuItem = { path: surface.path, label: surface.placement.label }
    items.push(item)
    byPath.set(surface.path, item)
  }

  for (const [parentPath, children] of Object.entries(extraChildren)) {
    const parent = byPath.get(parentPath)
    if (!parent || children.length === 0) continue
    parent.children = [...(parent.children ?? []), ...children]
  }

  for (const surface of SETTINGS_SURFACES) {
    if (surface.placement.kind !== 'child') continue
    const parent = byPath.get(surface.placement.under)
    // A child whose parent is not a top-level row would silently vanish. The guard
    // rejects that arrangement, so this is unreachable rather than tolerated.
    if (!parent) continue
    parent.children = [
      ...(parent.children ?? []),
      { path: surface.path, label: surface.placement.label },
    ]
  }

  return items
}
